import React, {useCallback, useMemo} from 'react';

import ColumnFilterDropdown from 'nav/expanded/ColumnFilterDropdown';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {getPrimaryLinkField} from 'results/PrimaryLinks';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';

import {DisplayConfigWithoutReviewed} from './AttachmentConstants';
import {getSpecialColumns} from './Columns';

function getHiddenColumnKeys(displayConfig: DisplayConfigWithoutReviewed): Array<string> {
  const hiddenColumnKeys: Array<string> = [];
  if (displayConfig.hideId) {
    hiddenColumnKeys.push('hideId');
  }
  if (displayConfig.hideCreator) {
    hiddenColumnKeys.push('hideCreator');
  }
  if (displayConfig.hideCreatedAt) {
    hiddenColumnKeys.push('hideCreatedAt');
  }
  hiddenColumnKeys.push(...displayConfig.hiddenFields);
  return hiddenColumnKeys;
}

export interface DisplayConfigDropdownProps {
  displayConfig: DisplayConfigWithoutReviewed;
  schema: AssaySchema;
  updateDisplayConfigHandler: (schemaId: string, displayConfig: DisplayConfigWithoutReviewed) => void;
}

export default function DisplayConfigDropdown({
  displayConfig,
  schema,
  updateDisplayConfigHandler,
}: DisplayConfigDropdownProps): JSX.Element {
  const specialColumns: Array<ScrollableTableColumn> = useMemo(() => {
    const cols = getSpecialColumns();
    return cols.map((col) => ({header: col.name, accessorKey: col.isHiddenKey}));
  }, []);
  const primaryLinkField = getPrimaryLinkField(schema);
  const schemaColumns = schema.fields
    .filter((field) => field !== primaryLinkField)
    .map((field) => ({
      header: field.displayName,
      accessorKey: field.name,
    }));

  const onUpdateHiddenColumns = useCallback(
    (hiddenColumnKeys: Array<string>) => {
      const newDisplayConfig: DisplayConfigWithoutReviewed = {
        hideId: false,
        hideCreator: false,
        hideCreatedAt: false,
        hiddenFields: [],
      };
      for (const columnKey of hiddenColumnKeys) {
        if (columnKey === 'hideId') {
          newDisplayConfig.hideId = true;
        } else if (columnKey === 'hideCreator') {
          newDisplayConfig.hideCreator = true;
        } else if (columnKey === 'hideCreatedAt') {
          newDisplayConfig.hideCreatedAt = true;
        } else {
          newDisplayConfig.hiddenFields.push(columnKey);
        }
      }
      updateDisplayConfigHandler(schema.id, newDisplayConfig);
    },
    [schema.id, updateDisplayConfigHandler]
  );

  return (
    <ColumnFilterDropdown
      columns={[...specialColumns, ...schemaColumns]}
      hiddenColumnKeys={getHiddenColumnKeys(displayConfig)}
      onUpdateHiddenColumns={onUpdateHiddenColumns}
    />
  );
}
